// src/oura/gbp/model.ts
//
// The Google profile form, in words and numbers only: what a stored field
// looks like in an input, what goes back to the server, and what is wrong with
// it before anything is sent (#4010).
//
// The rules here MIRROR `clean_fields` in the backend's `oura_gbp.py`, which is
// the authority. They exist so a mistake lands next to the field that caused
// it: the server's refusal names the field's LABEL and not its key, so a
// round trip can only ever be placed by matching that sentence back to a label
// (`fieldErrorKey`). Checking first means the common mistakes never need that.

import type { GbpField, GbpOutlet, GbpValue } from "@service/aeo/gbpProfile";

/**
 * One field as the person edits it: always a string, including the numbers
 * and the yes/no.
 *
 * A number kept as a number could not hold "4." mid-typing, and a bool kept as
 * a boolean has nowhere to be "not recorded" — which is a real third state
 * here, and the one every field starts in.
 */
export type GbpDraft = Record<string, string>;

/** A bool's three states, as the radio group's values. */
export const BOOL_YES = "yes";
export const BOOL_NO = "no";

/** What a stored value looks like in its input. An absent key is the empty string: not recorded. */
export function draftValue(field: GbpField, value: GbpValue | undefined): string {
    if (value === undefined || value === null) return "";
    if (field.kind === "bool") return value === true ? BOOL_YES : value === false ? BOOL_NO : "";
    if (field.kind === "list") return Array.isArray(value) ? value.join("\n") : String(value);
    return String(value);
}

/** The whole form for one outlet, from what the server has. */
export function draftFrom(fieldList: GbpField[], outlet: GbpOutlet | null): GbpDraft {
    const draft: GbpDraft = {};
    for (const field of fieldList) draft[field.key] = draftValue(field, outlet?.fields[field.key]);
    return draft;
}

/** The lines of a `list` field: blank lines dropped, inner runs of space collapsed, as the server stores them. */
export function listLines(text: string): string[] {
    return text
        .split("\n")
        .map((line) => line.split(/\s+/).filter(Boolean).join(" "))
        .filter(Boolean);
}

/**
 * What is wrong with one field, in the words the server would use, or null.
 *
 * Deliberately the same sentences as `clean_fields`, so a check that happens
 * here and a check that happens there do not read like two different products.
 */
export function checkField(field: GbpField, raw: string): string | null {
    const text = raw.trim();
    if (!text) return null; // Blank is fine everywhere: it reads as not recorded.
    if (field.kind === "bool") return text === BOOL_YES || text === BOOL_NO ? null : `${field.label} is yes or no.`;
    if (field.kind === "number") {
        const n = Number(text);
        if (!/^\d*\.?\d+$/.test(text) || !Number.isFinite(n)) return `${field.label} is a number.`;
        if (field.key === "rating" && n > 5) return "The rating is out of 5.";
        return null;
    }
    if (field.kind === "list") {
        const lines = listLines(raw);
        if (field.limit !== null && lines.length > field.limit) return `${field.label}: at most ${field.limit} lines.`;
        return null;
    }
    if (field.limit !== null && text.length > field.limit) return `${field.label}: at most ${field.limit} characters.`;
    return null;
}

/** Every field's complaint, by key. Empty when the form is ready to send. */
export function checkDraft(fieldList: GbpField[], draft: GbpDraft): Record<string, string> {
    const out: Record<string, string> = {};
    for (const field of fieldList) {
        const bad = checkField(field, draft[field.key] ?? "");
        if (bad) out[field.key] = bad;
    }
    return out;
}

/**
 * The body's `fields`: every field that has a value, in the server's own types.
 *
 * Blanks are left OUT rather than sent as "" or []: the server drops them
 * either way, and sending them would claim they were answered.
 */
export function fieldsFrom(fieldList: GbpField[], draft: GbpDraft): Record<string, GbpValue> {
    const out: Record<string, GbpValue> = {};
    for (const field of fieldList) {
        const raw = draft[field.key] ?? "";
        const text = raw.trim();
        if (!text) continue;
        if (field.kind === "bool") {
            if (text === BOOL_YES || text === BOOL_NO) out[field.key] = text === BOOL_YES;
        } else if (field.kind === "number") {
            const n = Number(text);
            if (Number.isFinite(n)) out[field.key] = n;
        } else if (field.kind === "list") {
            const lines = listLines(raw);
            if (lines.length) out[field.key] = lines;
        } else {
            out[field.key] = text;
        }
    }
    return out;
}

/** Whether the form differs from what the server has, so Save is only offered when there is something to save. */
export function draftChanged(fieldList: GbpField[], outlet: GbpOutlet | null, draft: GbpDraft): boolean {
    return stable(fieldsFrom(fieldList, draft)) !== stable(outlet?.fields ?? {});
}

/**
 * Two records of the same answers, compared as equal.
 *
 * BOTH sides are sorted, not just the stored one: `fieldsFrom` writes its keys
 * in the field list's order and the server answers in its own, so comparing
 * them as written made every untouched form look edited and offered Save on a
 * screen nobody had typed into.
 */
function stable(fields: Record<string, GbpValue>): string {
    return JSON.stringify(Object.keys(fields).sort().map((key) => [key, fields[key]]));
}

/**
 * Which field a server refusal is about, or null for one that is about none.
 *
 * The refusal names the field's label ("Description, word for word: at most
 * 1000 characters."), and the labels come from the same payload as the form,
 * so this matches the server's own strings rather than guessing at literals.
 * Longest label first: "Photos and videos" must not be claimed by a shorter
 * label that happens to be its prefix.
 */
export function fieldErrorKey(fieldList: GbpField[], message: string): string | null {
    const text = message.trim().toLowerCase();
    const byLength = [...fieldList].sort((a, b) => b.label.length - a.label.length);
    return byLength.find((f) => text.startsWith(f.label.toLowerCase()))?.key ?? null;
}

/** "4 of 17 recorded": how much of the profile is on file. */
export function recordedCount(fieldList: GbpField[], outlet: GbpOutlet | null): { done: number; total: number } {
    const fields = outlet?.fields ?? {};
    return { done: fieldList.filter((f) => fields[f.key] !== undefined).length, total: fieldList.length };
}

/** "before_description" as "Description". `other` keeps its own word. */
export function shotLabelText(label: string): string {
    const bare = label.startsWith("before_") ? label.slice("before_".length) : label;
    return bare.charAt(0).toUpperCase() + bare.slice(1).replace(/_/g, " ");
}

/**
 * The counter under a field, or null when it has nothing to count.
 *
 * A `text` limit counts characters and a `list` limit counts lines — the same
 * number in the payload means two different things, so the kind decides which.
 */
export function counterText(field: GbpField, raw: string): string | null {
    if (field.limit === null) return null;
    if (field.kind === "list") return `${listLines(raw).length} of ${field.limit} lines`;
    if (field.kind === "text") return `${raw.trim().length} of ${field.limit} characters`;
    return null;
}
